import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ShieldAlert, CheckCircle2, X } from "lucide-react";
import { useShipperCreditProfile } from "@/hooks/useShipperCreditProfile";
import { CompleteCreditProfileModal } from "./CompleteCreditProfileModal";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Props {
  companyId: string;
  canEdit: boolean;
}

export function CreditProfileBanner({ companyId, canEdit }: Props) {
  const { user } = useAuth();
  const { profile, completion, refresh, loading, loaded } = useShipperCreditProfile(companyId);
  const [open, setOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [dismissedAt, setDismissedAt] = useState<string | null>(null);
  const [dismissalLoaded, setDismissalLoaded] = useState(false);

  const dismissalKey = `credit_profile_ready:${companyId}`;

  // Load per-user dismissal for this banner.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user?.id || !companyId) {
        setDismissalLoaded(true);
        return;
      }
      const { data, error } = await supabase
        .from("user_ui_dismissals")
        .select("dismissed_at")
        .eq("user_id", user.id)
        .eq("key", dismissalKey)
        .maybeSingle();
      if (cancelled) return;
      if (error) console.error("[CreditProfileBanner] dismissal read failed:", error);
      setDismissedAt(data?.dismissed_at ?? null);
      setDismissalLoaded(true);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [user?.id, companyId, dismissalKey]);

  if (!loaded || loading || !dismissalLoaded) return null;

  // Success banner intentionally removed — the completion status is surfaced
  // elsewhere (dashboard indicator + Company Settings → Credit Profile tab).
  // We only render the incomplete/CTA state here.
  if (completion.isComplete) return null;

  return (
    <>
      <Card className="p-4 border-amber-300/60 bg-amber-50 dark:bg-amber-950/20 flex items-center gap-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-amber-100 dark:bg-amber-900/40">
          <ShieldAlert className="h-5 w-5 text-amber-700 dark:text-amber-400" />
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-3">
            <p className="text-sm font-medium text-foreground">
              Complete your credit profile to unlock faster broker setup
            </p>
            <span className="text-xs text-muted-foreground">{completion.percent}%</span>
          </div>
          <Progress value={completion.percent} className="h-1.5 max-w-md" />
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => setOpen(true)}>
            Complete profile
          </Button>
        )}
      </Card>
      {canEdit && (
        <CompleteCreditProfileModal
          open={open}
          onOpenChange={setOpen}
          companyId={companyId}
          onSaved={refresh}
        />
      )}
    </>
  );
}
